import Reveal3 from '@/components/marketing3/Reveal3'
import TargetGauge3 from '@/components/marketing3/widgets/TargetGauge3'

/**
 * Targets3 (light section with a dark instrument-panel inset) — the differentiator.
 * Two-column layout: locked-vocabulary prose + the TargetGauge (W8), a room of night
 * floating in daylight (the page's hinge motif).
 *
 * Copy is quoted VERBATIM from docs/marketing/2026-07-05-landing3-copy.md §Targets and
 * uses ONLY the locked Target vocabulary (docs/target-vocabulary.md): Pushing / Easing /
 * On track / ADR floor / "Targets are optional". None of the retired Target-context
 * synonyms appear (see docs/target-vocabulary.md). No PMS name.
 *
 * Tuned down 2026-07-05 (founder direction): engine-internal terms and their when-it-acts
 * threshold logic were removed; the safeguards paragraph is now a plain limits-you-control
 * promise (ADR floor kept — it is a user-facing setting). Canonical Pushing / Easing /
 * On track state definitions preserved. See the copy doc's §Targets and claims-register §3
 * for what was retired and why.
 */
export default function Targets3({ brand }: { brand: string }) {
  return (
    <section id="targets" className="scroll-mt-24" aria-labelledby="targets-heading">
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-24 sm:px-6 sm:py-28 lg:grid-cols-2 lg:gap-16">
        <div>
          <Reveal3>
            <p className="mkt3-eyebrow">Targets</p>
            <h2 id="targets-heading" className="mkt3-display mkt3-h2 mt-5">
              Commit to a month. The engine steers.
            </h2>
          </Reveal3>
          <Reveal3 delay={100}>
            <div className="mt-6 space-y-5">
              <p className="mkt3-prose">
                Most pricing tools only react. {brand} can also steer. Save a monthly target — occupancy, ADR or
                RevPAR — and the engine projects where the month is heading, compares it to your target, and steers
                your rates to close the gap.
              </p>
              <p className="mkt3-prose">
                Below target it starts <strong>Pushing</strong> — lowering rates to bring bookings in. Above
                target it starts <strong>Easing</strong> — lifting rates to earn more from the demand you have. Right
                on your target you&rsquo;re <strong>On track</strong>, and it leaves well alone.
              </p>
              <p className="mkt3-prose">
                And it steers within limits you set. It will never discount below your <strong>ADR floor</strong>,
                and it never buys occupancy at the cost of rate integrity — chasing bookings never quietly cheapens
                rooms you&rsquo;d have sold anyway.
              </p>
              <p className="mkt3-prose">
                <strong>Targets are optional.</strong> Save one and the engine steers toward it. Save none and it
                prices purely on demand — nothing changes.
              </p>
            </div>
          </Reveal3>
        </div>

        <Reveal3 delay={160}>
          <TargetGauge3 />
        </Reveal3>
      </div>
    </section>
  )
}
